import { alpha, createTheme } from '@mui/material/styles';

const accent = '#F7931A';
const canvas = '#0B0E14';
const surface = '#171A21';
const surfaceRaised = '#1B2838';

const focusVisible = {
  outline: `2px solid ${accent}`,
  outlineOffset: `2px`,
};

const v2Theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: accent,
      light: '#FF9F30',
      contrastText: '#07090D',
    },
    secondary: {
      main: '#3EA6FF',
    },
    background: {
      default: canvas,
      paper: surface,
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#A3A3A3',
      disabled: '#737373',
    },
    divider: 'rgba(255,255,255,0.08)',
    error: {
      main: '#EF4444',
      light: '#F87171',
    },
    success: {
      main: '#10B981',
      light: '#34D399',
    },
  },
  typography: {
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: {
      fontSize: 'clamp(2rem, 5vw, 3rem)',
      fontWeight: 900,
      fontStyle: 'italic',
      letterSpacing: '-0.04em',
      lineHeight: 1,
      textTransform: 'uppercase',
    },
    h2: {
      fontSize: 'clamp(1.65rem, 4vw, 2.15rem)',
      fontWeight: 800,
      letterSpacing: '-0.03em',
      lineHeight: 1.1,
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    button: {
      fontWeight: 800,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
    },
    overline: {
      fontFamily:
        '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      fontSize: '0.625rem',
      fontWeight: 700,
      letterSpacing: '0.16em',
      lineHeight: 1.4,
    },
  },
  shape: {
    borderRadius: 8,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },
  shadows: [
    'none',
    '0 10px 30px rgba(0,0,0,0.18)',
    '0 18px 45px rgba(0,0,0,0.22)',
    '0 25px 50px -12px rgba(0,0,0,0.25)',
    ...Array(21).fill('0 25px 50px -12px rgba(0,0,0,0.25)'),
  ],
  components: {
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&:focus-visible': focusVisible,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          minHeight: 44,
          borderRadius: 8,
          paddingInline: 18,
          transition: 'background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease',
          '&:focus-visible': focusVisible,
        },
        containedPrimary: {
          boxShadow: `0 0 15px ${alpha(accent, 0.3)}`,
          '&:hover': {
            backgroundColor: '#FF9F30',
            boxShadow: `0 0 20px ${alpha(accent, 0.55)}`,
          },
        },
        outlined: {
          borderColor: 'rgba(255,255,255,0.12)',
          '&:hover': {
            borderColor: alpha(accent, 0.45),
            backgroundColor: alpha(accent, 0.08),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: 12,
        },
      },
      variants: [
        {
          props: { variant: 'v2-raised' },
          style: {
            backgroundColor: surfaceRaised,
          },
        },
      ],
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontFamily:
            '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: '0.625rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255,255,255,0.06)',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

export const v2Tokens = {
  accent,
  canvas,
  surface,
  surfaceRaised,
  surfaceInset: '#07090D',
  radius: {
    control: '8px',
    nested: '10px',
    panel: '12px',
  },
};

export default v2Theme;
